import React, { useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { useStyles } from './Styles';
import './Styles.scss';
import { useTranslation } from 'react-i18next';
import { useAuthentication } from '../../Providers/AuthenticationProvider';
import { MultifactorSetting } from '../../api/multifactor';
import { Application } from '../../api/application';
import { MultiFactorProps } from './Types';
import { urlJoin } from 'url-join-ts';
import settings from '../../config/settings';
import parse from 'html-react-parser';
import { getApplications } from '../../api/user';
import { isMobileDevice } from '../../utils';
import { Profile } from '../../store/profile/types';
import i18n from "i18next";

const getUserApplications = async (getTokenSilently: Function, profile: Profile | undefined, setApplications: Function, setIsLoading: Function, pageLanguage: string) => {
    // Get user applications in order to display the ones that uses MFA

    await getTokenSilently().then((result: string) => {

        getApplications(((profile && profile.sub) || ''), result, pageLanguage).then(userApplications => {

            const applicationsUsingMFA = userApplications.user_applications.reduce((appsUsingMFA: string[], application: Application) => {
                if (application.uses_mfa) {
                    appsUsingMFA.push((application.translations?.name || application.name || ''));
                }

                return appsUsingMFA;
            }, []);

            setApplications(applicationsUsingMFA as []);
        }).finally(() => {
            setIsLoading(false);
        })
    })
}

const setMFASettingsTypeDescription = (mfaSettings: MultifactorSetting[], setMFATypeDescription: Function, t: Function) => {

    const mfaSettingsFiltered = mfaSettings.filter((mfaSetting: MultifactorSetting) => mfaSetting.confirmed);

    mfaSettingsFiltered.forEach((mfaSetting: MultifactorSetting) => {
        if (mfaSetting.type === 'guardian' && mfaSetting.confirmed) {
            setMFATypeDescription(t("privacy_and_security.mfa_settings.mfa_types.guardian"));
        } else if (mfaSetting.type === 'totp' && mfaSetting.confirmed) {
            setMFATypeDescription(t("privacy_and_security.mfa_settings.mfa_types.one_time_password"));
        } else if (mfaSetting.type === 'phone' && mfaSetting.confirmed) {
            setMFATypeDescription(t('privacy_and_security.mfa_settings.mfa_types.sms', { property_phonenumber: mfaSetting.name }));
        }
    });
}

const MultiFactor: React.FC<MultiFactorProps> = ({ profile, mfaSettings, cultures, setShowRecoveryCodePasswordConfim, recoveryCodeSent, systemMaintenanceSettings, isPageDownForMaintenance }) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const [isLoading, setIsLoading] = useState(true);

    const [mfaTypeDescription, setMFATypeDescription] = useState('');
    const [applications, setApplications] = useState([])

    const { getTokenSilently, loginWithRedirect } = useAuthentication();

    useEffect(() => {
        getUserApplications(getTokenSilently, profile, setApplications, setIsLoading, cultures.currentCulture);
    }, []);

    useEffect(() => {
        setMFASettingsTypeDescription(mfaSettings, setMFATypeDescription, t);
    }, [mfaSettings, i18n.language]);

    const loginWithMFA = () => {
        loginWithRedirect(
            {
                appState: { redirectUri: '/mfaresetconfirm' },
                ui_locales: (cultures && cultures.currentCulture) || 'en',
                acr_values: "http://schemas.openid.net/pape/policies/2007/06/multi-factor",
                scope: "user-mfa-deletion.write"
            }
        );
    }

    const sendRecoveryCode = () => {
        setShowRecoveryCodePasswordConfim(true);
    }

    const getFAQLinkText = () => {
        const translation = t('privacy_and_security.mfa_settings.faq_link');
        return `${translation.split("<faq>")[0]}<a href="${settings.faq.faq_mfa_endpoint}" target="_blank" rel="noopener noreferrer" className="${classes.link}" data-testid="faq">${translation.split("<faq>")[1]}</a>`;
    }

    if (mfaSettings.length === 0) {
        return null;
    }

    return !isLoading ? (
        <Grid container direction="row" className={classes.MFAOuterContainer}>
            {isPageDownForMaintenance(systemMaintenanceSettings, classes.maintenanceAlertPopup, classes.maintenanceAlertPopupContainer, classes.maintenanceAlertOverlay)}
            <Grid item xs={12}>
                <Grid container item xs={12} spacing={3}>
                    <Grid item xs={12} md={6} className={classes.borderRight}>
                        {!isMobileDevice() && (
                            <Typography className={`${classes.subHeader} ${classes.spacerBottom}`}>
                                {t('privacy_and_security.mfa_settings.sub_header')}
                            </Typography>
                        )}
                        <Grid item xs={12} className={`${classes.descriptionText} ${classes.spacerTop}`}>
                            <span className={classes.DescriptionTextBold}>{t('privacy_and_security.mfa_settings.mfa_status')}:</span> {t('privacy_and_security.mfa_settings.mfa_status_enabled')}
                        </Grid>
                        <Grid item xs={12} className={`${classes.descriptionText} ${classes.spacerTop} ${applications.length === 0 ? classes.spacerBottom : ''}`}>
                            <span className={classes.DescriptionTextBold}>{t('privacy_and_security.mfa_settings.types_description')}:</span> {mfaTypeDescription}
                        </Grid>
                        {applications.length > 0 && (
                            <Grid item xs={12} className={`${classes.descriptionText} ${classes.spacerTop} ${classes.spacerBottom}`}>
                                <span className={classes.DescriptionTextBold}>{t('privacy_and_security.mfa_settings.application_description')}:</span> {applications.join(', ')}
                            </Grid>
                        )}
                        <Grid item xs={12} className={`${classes.descriptionText} ${classes.spacerTop}`}>
                            {parse(getFAQLinkText())}
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={6} >
                        <Typography className={`${classes.subHeader} ${classes.spacerBottom}`}>
                            {parse(t('privacy_and_security.mfa_settings.reset_mfa_settings_header_normal_login'))}
                        </Typography>
                        <Grid item xs={12} className={classes.descriptionText}>
                            {parse(t('privacy_and_security.mfa_settings.reset_mfa_settings_text_normal_login'))}
                        </Grid>
                        <Grid item xs={12} className={classes.MFALinkContainer}>
                            <button onClick={() => { loginWithMFA() }} className={classes.linkButton}>
                                <img src={urlJoin(settings.staticFiles.endpoint, '/images/system/LinkArrow.svg')} className={classes.linkArrow} alt="" /> {parse(t('privacy_and_security.mfa_settings.login_with_mfa_link_text'))}
                            </button><br />
                            <span className={classes.marginLeft30px}>
                                ({t('privacy_and_security.mfa_settings.login_with_mfa_link_text_note')})
                            </span>
                        </Grid>
                        <Typography className={`${classes.subHeader} ${classes.spacerBottom} ${classes.spacerTop}`}>
                            {parse(t('privacy_and_security.mfa_settings.lost_device_text'))}
                        </Typography>
                        <Grid item xs={12} className={`${classes.MFALinkText} ${classes.descriptionText}`}>
                            {t('privacy_and_security.mfa_settings.send_recovery_code_text')}
                        </Grid>
                        <Grid item xs={12} className={classes.MFALinkContainer}>
                            {!recoveryCodeSent.current && (
                                <button onClick={() => { sendRecoveryCode() }} className={classes.linkButton}>
                                    <img src={urlJoin(settings.staticFiles.endpoint, '/images/system/LinkArrow.svg')} className={classes.linkArrow} alt="" /> {t('privacy_and_security.mfa_settings.send_recovery_link_text')}
                                </button>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    ) : null;
}

export default MultiFactor;